import { passwordSchema, ROLES, userCreateSchema, type Role } from '@hr/shared';
import { formatDateTime } from '@hr/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { z } from 'zod';
import { PlusIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Field } from '@/components/form';
import { DataTable } from '@/components/data-table';
import { EmptyState, PageHeader } from '@/components/page';
import { MailboxPanel } from '@/features/mail/mailbox-panel';
import { InviteResult } from '@/features/users/invite-result';
import { SearchInput } from '@/components/search-input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ROLE_LABELS, uk } from '@/i18n/uk';
import {
  listSearchShape,
  tableState,
  tableStateToSearch,
  type ListSearch,
} from '@/lib/search-params';
import { errorMessage, trpc } from '@/lib/trpc';
import { useLatest } from '@/lib/use-latest';
import type { RouterOutputs } from '@/lib/types';

type UserRow = RouterOutputs['users']['list']['items'][number];
type Invite = RouterOutputs['users']['invite'];

export const Route = createFileRoute('/_app/users')({
  validateSearch: z.object(listSearchShape),
  beforeLoad: ({ context }) => {
    if (context.me.role !== 'admin') throw redirect({ to: '/' });
  },
  component: UsersPage,
});

function UsersPage() {
  const { me } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const qc = useQueryClient();
  const state = tableState(search, { sort: 'fullName', dir: 'asc' });
  const setSearch = (patch: Partial<ListSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const users = useQuery({
    ...trpc.users.list.queryOptions({
      page: state.page,
      pageSize: state.pageSize,
      sort: state.sort,
      dir: state.dir,
      q: search.q,
    }),
    placeholderData: keepPreviousData,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [invited, setInvited] = useState<{ email: string; invite: Invite } | null>(null);

  const update = useMutation(
    trpc.users.update.mutationOptions({
      onSuccess: () => toast.success(uk.common.saved),
      onError: (e) => toast.error(errorMessage(e)),
      onSettled: () => qc.invalidateQueries(trpc.users.pathFilter()),
    }),
  );

  const invite = useMutation(
    trpc.users.invite.mutationOptions({
      onSuccess: (r, v) => {
        const email = users.data?.items.find((u) => u.id === v.id)?.email ?? '';
        if (r.sent) toast.success(uk.invite.sent(email));
        else setInvited({ email, invite: r });
      },
      onError: (e) => toast.error(errorMessage(e)),
      onSettled: () => qc.invalidateQueries(trpc.users.pathFilter()),
    }),
  );

  const actions = useLatest({ update, invite, setEditing });
  // Колонки стабільні: flexRender вважає функцію cell компонентом.
  const columns = useMemo<ColumnDef<UserRow>[]>(
    () => [
      {
        id: 'fullName',
        header: uk.users.fullName,
        cell: ({ row }) => (
          <span className="font-medium">
            {row.original.fullName}
            {row.original.id === me.id && (
              <span className="ml-2 text-xs text-muted-foreground">({uk.users.you})</span>
            )}
          </span>
        ),
      },
      { id: 'email', header: uk.users.email, accessorKey: 'email' },
      {
        id: 'role',
        header: uk.users.role,
        cell: ({ row: { original: u } }) => (
          <div className="flex flex-wrap gap-1">
            <Badge variant="secondary">{ROLE_LABELS[u.role]}</Badge>
            {!u.isActive && <Badge variant="outline">{uk.users.inactive}</Badge>}
            {u.isActive && u.invitePending && <Badge variant="outline">{uk.users.invited}</Badge>}
            {u.lockedUntil && u.lockedUntil > new Date() && (
              <Badge variant="destructive">
                {uk.users.locked} {formatDateTime(u.lockedUntil)}
              </Badge>
            )}
          </div>
        ),
      },
      {
        id: 'actions',
        header: uk.common.actions,
        enableSorting: false,
        cell: ({ row: { original: u } }) => (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => actions.current.setEditing(u)}>
              {uk.common.edit}
            </Button>
            {u.id !== me.id && u.isActive && (
              <Button
                size="sm"
                variant="ghost"
                disabled={actions.current.invite.isPending}
                onClick={() => actions.current.invite.mutate({ id: u.id })}
              >
                {uk.users.invite}
              </Button>
            )}
            {u.id !== me.id && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => actions.current.update.mutate({ id: u.id, isActive: !u.isActive })}
              >
                {u.isActive ? uk.users.deactivate : uk.users.activate}
              </Button>
            )}
          </div>
        ),
      },
    ],
    [me.id, actions],
  );

  return (
    <>
      <PageHeader
        title={uk.users.title}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <PlusIcon /> {uk.users.add}
          </Button>
        }
      />
      <div className="mb-3">
        <SearchInput
          value={search.q ?? ''}
          placeholder={uk.users.searchPlaceholder}
          onChange={(q) => setSearch({ q: q || undefined, page: undefined })}
        />
      </div>
      <DataTable
        columns={columns}
        data={users.data?.items}
        total={users.data?.total ?? 0}
        state={state}
        onStateChange={(next) => setSearch(tableStateToSearch(next))}
        isLoading={users.isPending}
        getRowId={(u) => u.id}
        rowClassName={(u) => (u.isActive ? undefined : 'opacity-60')}
        empty={<EmptyState title={uk.common.notFound} />}
      />

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} />
      <Dialog open={invited !== null} onOpenChange={(o) => !o && setInvited(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{uk.users.invite}</DialogTitle>
          </DialogHeader>
          {invited && <InviteResult email={invited.email} invite={invited.invite} />}
          <DialogFooter>
            <Button onClick={() => setInvited(null)}>{uk.common.close}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {editing && (
        <EditUserDialog
          key={editing.id}
          user={editing}
          isSelf={editing.id === me.id}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function RoleSelect({
  value,
  onChange,
  disabled,
}: {
  value: Role;
  onChange: (role: Role) => void;
  disabled?: boolean;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as Role)} disabled={disabled}>
      <SelectTrigger id="role" className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {ROLES.map((r) => (
          <SelectItem key={r} value={r}>
            {ROLE_LABELS[r]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function CreateUserDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const form = useForm({
    resolver: zodResolver(createSchema),
    defaultValues: { email: '', fullName: '', role: 'recruiter' as Role, password: '' },
  });
  const [result, setResult] = useState<{ email: string; invite: Invite } | null>(null);
  const close = (o: boolean) => {
    if (!o) {
      setResult(null);
      form.reset();
    }
    onOpenChange(o);
  };
  const create = useMutation(
    trpc.users.create.mutationOptions({
      onSuccess: (r) => {
        toast.success(uk.users.created);
        if (r.invite) setResult({ email: r.email, invite: r.invite });
        else close(false);
      },
      onError: (e) => toast.error(errorMessage(e)),
      onSettled: () => qc.invalidateQueries(trpc.users.pathFilter()),
    }),
  );
  const errors = form.formState.errors;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{uk.users.add}</DialogTitle>
        </DialogHeader>
        {result ? (
          <InviteResult email={result.email} invite={result.invite} />
        ) : (
          <form
            id="create-user"
            onSubmit={form.handleSubmit(({ password, ...v }) =>
              create.mutate({ ...v, ...(password ? { password } : {}) }),
            )}
            className="space-y-3"
            noValidate
          >
            <Field label={uk.users.fullName} htmlFor="fullName" error={errors.fullName}>
              <Input id="fullName" {...form.register('fullName')} />
            </Field>
            <Field label={uk.users.email} htmlFor="email" error={errors.email}>
              <Input id="email" type="email" {...form.register('email')} />
            </Field>
            <Field label={uk.users.role} htmlFor="role" error={errors.role}>
              <Controller
                control={form.control}
                name="role"
                render={({ field }) => <RoleSelect value={field.value} onChange={field.onChange} />}
              />
            </Field>
            <Field
              label={uk.users.password}
              htmlFor="password"
              error={errors.password}
              hint={uk.users.passwordOptional}
            >
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                {...form.register('password')}
              />
            </Field>
          </form>
        )}
        <DialogFooter>
          {result ? (
            <Button onClick={() => close(false)}>{uk.common.close}</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => close(false)}>
                {uk.common.cancel}
              </Button>
              <Button type="submit" form="create-user" disabled={create.isPending}>
                {create.isPending ? uk.common.saving : uk.common.create}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const optionalPassword = passwordSchema.or(z.literal(''));
const createSchema = userCreateSchema.extend({ password: optionalPassword });
const editSchema = userCreateSchema.pick({ fullName: true, role: true }).extend({
  password: optionalPassword,
});

function EditUserDialog({
  user,
  isSelf,
  onClose,
}: {
  user: { id: string; fullName: string; role: Role };
  isSelf: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const form = useForm({
    resolver: zodResolver(editSchema),
    defaultValues: { fullName: user.fullName, role: user.role, password: '' },
  });
  const update = useMutation(
    trpc.users.update.mutationOptions({
      onSuccess: () => {
        toast.success(uk.common.saved);
        onClose();
      },
      onError: (e) => toast.error(errorMessage(e)),
      onSettled: () => qc.invalidateQueries(trpc.users.pathFilter()),
    }),
  );
  const errors = form.formState.errors;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{user.fullName}</DialogTitle>
        </DialogHeader>
        <form
          id="edit-user"
          onSubmit={form.handleSubmit((v) =>
            update.mutate({
              id: user.id,
              fullName: v.fullName,
              role: v.role,
              ...(v.password ? { password: v.password } : {}),
            }),
          )}
          className="space-y-3"
          noValidate
        >
          <Field label={uk.users.fullName} htmlFor="fullName" error={errors.fullName}>
            <Input id="fullName" {...form.register('fullName')} />
          </Field>
          <Field label={uk.users.role} htmlFor="role" error={errors.role}>
            <Controller
              control={form.control}
              name="role"
              render={({ field }) => (
                <RoleSelect value={field.value} onChange={field.onChange} disabled={isSelf} />
              )}
            />
          </Field>
          <Field
            label={uk.users.newPassword}
            htmlFor="password"
            error={errors.password}
            hint={uk.users.passwordHint}
          >
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              {...form.register('password')}
            />
          </Field>
        </form>
        <section className="space-y-2 border-t pt-3">
          <h3 className="text-sm font-medium">{uk.mailbox.title}</h3>
          <MailboxPanel
            userId={user.id}
            lastName={user.fullName.split(' ')[0] ?? ''}
            firstName={user.fullName.split(' ')[1] ?? ''}
          />
        </section>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {uk.common.cancel}
          </Button>
          <Button type="submit" form="edit-user" disabled={update.isPending}>
            {update.isPending ? uk.common.saving : uk.common.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
